import { useRouter } from 'next/router';
import { FC, useMemo, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import Button from '../Button';
import Drawer from '../Drawer';
import Text from '../Text';

import Colors from '~/constants/colors';
import { CartAtom } from '~/services/cart';
import { formatNumber } from '~/utils/number';
import QuickCartItem from '../Items/QuickCartItem';
import LoadingContent from '../LoadingContent';
import PaymentStripe from '../PaymentStripe';
import ProgressDiscount from '../ProgressDiscount';
import styles from './QuickCart.module.scss';
import { PATH } from '~/constants/enum';
import useWindowDimensions from '~/hooks/useWindowDimensions';
import { capitalizeFirstLetter } from '~/utils/format';
import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';

interface QuickCartProps {}

const QuickCart: FC<QuickCartProps> = () => {
    const router = useRouter();
    const setCart = useSetRecoilState(CartAtom.cart);
    const cartSelector = useRecoilValue(CartAtom.cartSelector);
    const { width } = useWindowDimensions();
    const [readyActions, setReadyActions] = useState(false);
    const [errorPaymentStripe, setErrorPaymentStripe] = useState<any>(false);

    const subtotal =
        cartSelector.items
            ?.map((item) => {
                const finalPrice = item.selectedVariant?.product?.price_range?.minimum_price?.final_price?.value || 0;
                const quantity = item.quantity || 1;

                return finalPrice * quantity;
            })
            .reduce((sum, item) => sum + item, 0) || 0;

    const displayItems = useMemo(() => {
        if (width > 1024) return cartSelector.items;
        const result = cartSelector.items?.slice(0, 2);
        return result;
    }, [width, cartSelector.items]);

    const isShowMore = useMemo(() => {
        return (cartSelector?.items?.length || 0) > 2 && width <= 1024;
    }, [cartSelector.items]);

    return (
        <Drawer
            width={width > 1024 ? 500 : '100%'}
            className={styles.quick_cart}
            overlayOpacity={0}
            open={!!cartSelector.open}
            onClose={() => setCart({ ...cartSelector, open: false })}
            title={
                <div className="w-full">
                    <div className="flex items-center flex-1 gap-2">
                        <Text type="title-3">My cart</Text>
                        <Text type="body-3">({cartSelector.items?.length}) </Text>
                    </div>
                </div>
            }
            direction={width > 1024 ? 'right' : 'bottom'}
            style={{ height: width > 1024 ? '100vh' : 'auto' }}
        >
            <div className=" w-full flex justify-center">
                <ProgressDiscount />
            </div>
            {!!cartSelector.loading && <LoadingContent text="Loading Cart..." />}
            <div className="flex-1 overflow-y-auto">
                {Array.isArray(displayItems) && displayItems.map((item) => <QuickCartItem key={item.id} data={item} />)}
            </div>
            {isShowMore && (
                <div
                    onClick={() => {
                        router.push(PATH.Carts);
                        setCart({ ...cartSelector, open: false });
                    }}
                    className="text-center underline"
                    style={{ color: Colors.PRIMARY }}
                >
                    Show more
                </div>
            )}
            <div className={styles.subtotal}>
                <div className="flex flex-col gap-1">
                    {!!cartSelector.prices?.discounts?.length && (
                        <div className="flex items-center justify-between gap-6">
                            <Text className="text-grey-800">Subtotal</Text>
                            <Text className="text-grey-800">${formatNumber(cartSelector.subtotal, 2)}</Text>
                        </div>
                    )}
                    {!!cartSelector.prices?.discounts?.length &&
                        cartSelector.prices?.discounts?.map((discount) => (
                            <div className="flex items-center justify-between gap-6">
                                <Text type="label-4" className="capitalize" color={Colors.GREY_900}>
                                    {capitalizeFirstLetter(discount.label || 'Discount')}
                                </Text>
                                <Text color={Colors.ACCENT_RED_50}>-${formatNumber(discount.amount?.value, 2)}</Text>
                            </div>
                        ))}
                    <div className="flex items-center justify-between gap-6">
                        <Text type="title-4">Total:</Text>
                        <Text type="title-4" className="text-primary">
                            ${formatNumber(cartSelector?.prices?.grand_total?.value || 0, 2)}
                        </Text>
                    </div>
                    {/* <Text>Tax included and shipping calculated at checkout</Text>
          <Checkbox
            label={
              <Text className="text-grey-800">
                I agree with Terms & Conditions
              </Text>
            }
          /> */}
                </div>
                {!readyActions && !errorPaymentStripe && (
                    <div className="flex gap-1">
                        <div className="flex-1">
                            <Skeleton height={40} width={'100%'} />
                        </div>
                        <div className="flex-1">
                            <Skeleton height={40} width={'100%'} />
                        </div>
                    </div>
                )}
                {
                    <div
                        className={classNames('flex flex-col gap-1', {
                            hidden: !readyActions && !errorPaymentStripe
                        })}
                    >
                        <div className="flex flex-row  gap-1 ">
                            <Button
                                type="outline"
                                className="flex-1 lg:flex-auto"
                                onClick={() => {
                                    setCart({ ...cartSelector, open: false });
                                }}
                            >
                                Continue shopping
                            </Button>
                            {!!errorPaymentStripe && (
                                <Button
                                    className="flex-1 lg:flex-auto"
                                    disabled={cartSelector.items?.length === 0}
                                    onClick={() => {
                                        setCart({ ...cartSelector, open: false });
                                        router.push('/checkout');
                                    }}
                                >
                                    Process to checkout
                                </Button>
                            )}
                            {subtotal > 0 && !errorPaymentStripe && (
                                <div className="flex-1">
                                    <PaymentStripe
                                        onReady={() => setReadyActions(true)}
                                        onError={(err) => {
                                            return setErrorPaymentStripe(err);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        {!errorPaymentStripe && (
                            <div className="text-center underline text-primary py-2">
                                <span
                                    onClick={() => {
                                        setCart({ ...cartSelector, open: false });
                                        router.push('/checkout');
                                    }}
                                >
                                    More to payment
                                </span>
                            </div>
                        )}
                    </div>
                }
            </div>
        </Drawer>
    );
};

export default QuickCart;
